import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sprout } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const formatError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || String(e)).join(" ");
  return String(detail);
};

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(form.email, form.password);
      navigate("/admin");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "bg-obsidian border border-line rounded-md px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus-ring w-full";

  return (
    <main data-testid="admin-login-page" className="pt-40 pb-32 min-h-screen">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="max-w-md mx-auto px-6"
      >
        <div className="border border-line bg-surface p-10 flex flex-col gap-5">
          <Sprout className="w-8 h-8 text-saffron" />
          <h1 className="font-display text-3xl text-zinc-100 tracking-tight">Farm Gate</h1>
          <p className="text-sm text-zinc-500">Family only — pre-orders and tour requests live in here.</p>
          <form data-testid="admin-login-form" onSubmit={submit} className="flex flex-col gap-4 mt-2">
            <input
              data-testid="admin-email-input"
              required
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={inputCls}
            />
            <input
              data-testid="admin-password-input"
              required
              type="password"
              placeholder="Password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className={inputCls}
            />
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-wine-light">{error}</p>
            )}
            <button
              data-testid="admin-login-submit"
              disabled={submitting}
              className="mt-2 rounded-full bg-saffron px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold text-obsidian hover:bg-saffron-hover transition-colors disabled:opacity-50 focus-ring"
            >
              {submitting ? "Opening the gate…" : "Sign In"}
            </button>
          </form>
        </div>
      </motion.div>
    </main>
  );
}
